/* ============================================================================
   SUTRAYAN — ORDER TRACKING (theme port of the headless /order-tracking page)
   Owns: the lookup form submit + results rendering for
   sections/sutrayan-order-tracking.liquid.

   Talks to the SAME backend as sutrayan.com: POST {endpoint}
   (default https://sutrayan.com/api/guest/order-tracking) with
   { orderNumber, email }. CORS/CSRF for checkout.sutrayan.com is allow-listed
   in the Next app's proxy.ts middleware.

   Response shape (parity with app/api/guest/order-tracking/route.ts):
     { orderId, orderNumber, status, carrier, trackingNumber, trackingUrl,
       estimatedDelivery, weight, packages, productImageUrl, productTitle,
       events: [{ id, label, description, timestamp, location, srStatus? }] }

   Design parity ports: components/tracking/{tracking-utils,JourneyTracker,
   ETACountdown,ShipmentCard,ActivityTimeline}.tsx — vanilla, no framework.
   All injected order data is HTML-escaped (esc) and URLs are scheme-checked
   (safeUrl) before use.
   ============================================================================ */
(function () {
  'use strict';

  /* ---- Inline lucide icons (stroke, 24x24) ------------------------------- */
  function icon(paths, cls) {
    return (
      '<svg class="' + (cls || '') + '" xmlns="http://www.w3.org/2000/svg" ' +
      'width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      paths + '</svg>'
    );
  }
  var ICONS = {
    package: '<path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>',
    check: '<path d="M20 6 9 17l-5-5"/>',
    checkCircle: '<circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>',
    truck: '<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>',
    mapPin: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>',
    home: '<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
    copy: '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
    externalLink: '<path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>',
    clock: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
    alertTriangle: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
    info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
    arrowLeft: '<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>'
  };

  /* ---- Safety helpers ---------------------------------------------------- */
  var ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return ESC_MAP[c];
    });
  }
  // Only permit http/https absolute URLs as hrefs — blocks javascript:/data:.
  function safeUrl(value) {
    if (!value) return '';
    var str = String(value).trim();
    // Normalize protocol-relative URLs (Shopify `asset_url` returns
    // `//cdn.shopify.com/...`) before validating so the tracking hero image
    // renders instead of falling back to the glyph (amp-F9). Still blocks
    // javascript:/data: because only http/https survive the test below.
    if (/^\/\//.test(str)) str = 'https:' + str;
    if (/^https?:\/\//i.test(str)) return str;
    return '';
  }

  /* ---- Date formatting (ported from tracking-utils.ts) ------------------- */
  function formatElegantDate(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  }
  function formatEventTimestamp(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  function formatRelativeETA(iso) {
    var target = new Date(iso);
    if (isNaN(target.getTime())) return { days: 0, isToday: false, isPast: true };
    var now = new Date();
    var todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var targetStart = new Date(target.getFullYear(), target.getMonth(), target.getDate());
    var diffDays = Math.round((targetStart.getTime() - todayStart.getTime()) / 86400000);
    if (diffDays < 0) return { days: diffDays, isToday: false, isPast: true };
    if (diffDays === 0) return { days: 0, isToday: true, isPast: false };
    return { days: diffDays, isToday: false, isPast: false };
  }

  /* ---- Step progression (ported from tracking-utils.ts deriveSteps) ------ */
  var STEP_DEFS = [
    { key: 'confirmed', label: 'Confirmed', icon: 'checkCircle' },
    { key: 'shipped', label: 'Shipped', icon: 'package' },
    { key: 'in_transit', label: 'In Transit', icon: 'truck' },
    { key: 'out_for_delivery', label: 'Out for Delivery', icon: 'mapPin' },
    { key: 'delivered', label: 'Delivered', icon: 'home' }
  ];
  function srStatusToStepIndex(sr) {
    var map = { '1': 1, '2': 1, '3': 2, '4': 3, '5': 4, '6': 2, '7': 2 };
    return map.hasOwnProperty(sr) ? map[sr] : -1;
  }
  function eventStatusToStepIndex(status) {
    var map = { delivered: 4, in_transit: 2, delayed: 2, exception: 2, info: 0 };
    return map.hasOwnProperty(status) ? map[status] : -1;
  }
  function deriveActiveIndex(events, fulfillmentStatus) {
    if (fulfillmentStatus === 'fulfilled') return 4;
    var activeIndex = 0;
    for (var i = 0; i < events.length; i++) {
      var idx = -1;
      if (events[i].srStatus) idx = srStatusToStepIndex(events[i].srStatus);
      if (idx < 0) idx = eventStatusToStepIndex(events[i].status);
      if (idx > activeIndex) activeIndex = idx;
    }
    return activeIndex;
  }

  var ACTIVITY_ICON = {
    delivered: 'checkCircle',
    in_transit: 'truck',
    exception: 'alertTriangle',
    delayed: 'clock',
    info: 'info'
  };

  /* ---- Result view builders ---------------------------------------------- */
  function heroHTML(t, heroImage) {
    var delivered = t.status === 'fulfilled';
    // Parity with the headless results hero: it always shows the static artisan
    // package image. Fall back to a package glyph if the asset URL is missing.
    var img = safeUrl(heroImage);
    var media = img
      ? '<img class="sy-track-hero__thumb" src="' + esc(img) + '" alt="Your artisan package" loading="lazy" width="144" height="144">'
      : '<span class="sy-track-hero__thumb sy-track-hero__thumb--icon">' + icon(ICONS.package) + '</span>';
    return (
      '<div class="sy-track-hero sy-reveal">' +
        '<div class="sy-track-hero__media">' + media + '</div>' +
        '<div class="sy-track-hero__text">' +
          '<p class="sy-track-eyebrow">Sutrayan</p>' +
          '<h2 class="sy-track-hero__title">' + esc(delivered ? 'Order Delivered' : 'Your Order is On Its Way') + '</h2>' +
          '<p class="sy-track-hero__order">Order ' + esc(t.orderNumber) + '</p>' +
        '</div>' +
      '</div>'
    );
  }

  function etaHTML(t) {
    if (!t.estimatedDelivery) return '';
    var eta = formatRelativeETA(t.estimatedDelivery);
    var date = formatElegantDate(t.estimatedDelivery);
    var top;
    if (eta.isToday) {
      top = '<div class="sy-track-eta__big sy-track-eta__today">Arriving Today</div>';
    } else if (eta.isPast) {
      top = '<div class="sy-track-eta__muted">Delivery expected</div>';
    } else {
      top =
        '<div class="sy-track-eta__count"><span>Arriving in</span>' +
        '<span class="sy-track-eta__big">' + esc(eta.days) + '</span>' +
        '<span>' + (eta.days === 1 ? 'day' : 'days') + '</span></div>';
    }
    return (
      '<div class="sy-track-card sy-track-eta sy-reveal">' + top +
        (date ? '<div class="sy-track-eta__date">' + esc(date) + '</div>' : '') +
      '</div>'
    );
  }

  function journeyHTML(t) {
    var events = t.events || [];
    var activeIndex = deriveActiveIndex(events, t.status);
    var maxIndex = Math.max(1, STEP_DEFS.length - 1);
    var progress = Math.max(0, Math.min(100, (activeIndex / maxIndex) * 100));

    var nodes = STEP_DEFS.map(function (step, i) {
      var isCompleted = i < activeIndex;
      var isActive = i === activeIndex;
      var state = isCompleted ? 'is-complete' : (isActive ? 'is-active' : 'is-upcoming');
      var glyph = isCompleted ? icon(ICONS.check) : icon(ICONS[step.icon]);
      var aria = isActive ? ' aria-current="step"' : '';
      return (
        '<li class="sy-track-step ' + state + '"' + aria + '>' +
          '<span class="sy-track-step__node">' + glyph + '</span>' +
          '<span class="sy-track-step__label">' + esc(step.label) + '</span>' +
        '</li>'
      );
    }).join('');

    return (
      '<div class="sy-track-card sy-reveal">' +
        '<div class="sy-track-journey" role="list" aria-label="Delivery progress">' +
          '<div class="sy-track-journey__rail"><span class="sy-track-journey__fill" style="--sy-track-progress:' + progress.toFixed(2) + '%"></span></div>' +
          '<ol class="sy-track-journey__steps">' + nodes + '</ol>' +
        '</div>' +
      '</div>'
    );
  }

  function shipmentHTML(t) {
    if (!t.trackingNumber && !t.carrier && !t.estimatedDelivery) return '';
    var url = safeUrl(t.trackingUrl);
    var courier = t.carrier ? esc(t.carrier) : '';
    var left =
      (t.carrier ? '<span class="sy-track-badge">' + courier + '</span>' : '') +
      (t.trackingNumber
        ? '<div><p class="sy-track-label">Tracking Number</p>' +
          '<button type="button" class="sy-track-awb" data-sy-copy="' + esc(t.trackingNumber) + '" aria-label="Copy tracking number">' +
          '<span class="sy-track-awb__code">' + esc(t.trackingNumber) + '</span>' +
          '<span class="sy-track-awb__icon">' + icon(ICONS.copy) + '</span></button></div>'
        : '') +
      ((t.weight || t.packages)
        ? '<p class="sy-track-meta">' +
          (t.weight ? '<span>' + esc(t.weight) + '</span>' : '') +
          (t.weight && t.packages ? '<span aria-hidden="true">•</span>' : '') +
          (t.packages ? '<span>' + esc(t.packages) + ' ' + (t.packages === 1 ? 'Package' : 'Packages') + '</span>' : '') +
          '</p>'
        : '');
    var right =
      (t.estimatedDelivery
        ? '<div><p class="sy-track-label">Estimated Delivery</p><p class="sy-track-ship__eta">' + esc(formatElegantDate(t.estimatedDelivery)) + '</p></div>'
        : '') +
      (url
        ? '<a class="sy-track-ship__link" href="' + esc(url) + '" target="_blank" rel="noopener noreferrer">' +
          '<span>Track on ' + (courier || 'Courier') + '</span>' + icon(ICONS.externalLink) + '</a>'
        : '');
    return (
      '<div class="sy-track-card sy-track-ship sy-reveal">' +
        '<div class="sy-track-ship__col">' + left + '</div>' +
        '<div class="sy-track-ship__col sy-track-ship__col--right">' + right + '</div>' +
      '</div>'
    );
  }

  function activityHTML(t) {
    var events = t.events || [];
    if (!events.length) {
      return (
        '<div class="sy-track-card sy-reveal">' +
          '<div class="sy-track-empty">' + icon(ICONS.package, 'sy-track-empty__icon') +
          '<p>No tracking events yet</p></div>' +
        '</div>'
      );
    }
    var rows = events.map(function (ev, i) {
      var statusKey = ev.status && ACTIVITY_ICON[ev.status] ? ev.status : 'info';
      var glyph = icon(ICONS[ACTIVITY_ICON[statusKey]]);
      var latest = i === 0 ? ' is-latest' : '';
      return (
        '<li class="sy-track-activity__row sy-track-activity--' + esc(statusKey) + latest + '" role="listitem">' +
          '<span class="sy-track-activity__icon">' + glyph + '</span>' +
          '<div class="sy-track-activity__body">' +
            '<p class="sy-track-activity__label">' + esc(ev.label) + '</p>' +
            (ev.description ? '<p class="sy-track-activity__desc">' + esc(ev.description) + '</p>' : '') +
            (ev.location ? '<p class="sy-track-activity__loc">' + icon(ICONS.mapPin) + esc(ev.location) + '</p>' : '') +
          '</div>' +
          '<time class="sy-track-activity__time">' + esc(formatEventTimestamp(ev.timestamp)) + '</time>' +
        '</li>'
      );
    }).join('');
    return (
      '<div class="sy-track-card sy-reveal">' +
        '<h3 class="sy-track-section-title">Shipment Activity</h3>' +
        '<ul class="sy-track-activity" role="list">' + rows + '</ul>' +
      '</div>'
    );
  }

  function footerActionsHTML() {
    return (
      '<div class="sy-track-actions">' +
        '<button type="button" class="sy-track-reset" data-sy-track-reset>' + icon(ICONS.arrowLeft) + ' Track Another Order</button>' +
        '<a class="sy-track-help-link" data-sy-track-help href="#">Need Help? &rarr;</a>' +
      '</div>'
    );
  }

  /* ---- Controller -------------------------------------------------------- */
  function initTracking(root) {
    var endpoint = root.getAttribute('data-endpoint') || 'https://sutrayan.com/api/guest/order-tracking';
    var contactUrl = root.getAttribute('data-contact-url') || '/pages/contact';
    var heroImage = root.getAttribute('data-hero-image') || '';
    var form = root.querySelector('[data-sy-track-form]');
    var formPanel = root.querySelector('[data-sy-track-form-panel]');
    var results = root.querySelector('[data-sy-track-results]');
    var errorBox = root.querySelector('[data-sy-track-error]');
    var orderInput = root.querySelector('[data-sy-track-order]');
    var emailInput = root.querySelector('[data-sy-track-email]');
    var submitBtn = root.querySelector('[data-sy-track-submit]');
    if (!form || !formPanel || !results || !orderInput || !emailInput || !submitBtn) return;

    var submitLabel = submitBtn.querySelector('[data-sy-track-submit-label]') || submitBtn;
    var idleText = submitLabel.textContent;
    var errorText = errorBox ? (errorBox.querySelector('[data-sy-track-error-text]') || errorBox) : null;
    var busy = false;

    function showError(msg) {
      if (!errorBox) return;
      errorText.textContent = msg;
      errorBox.hidden = false;
    }
    function clearError() {
      if (!errorBox) return;
      errorText.textContent = '';
      errorBox.hidden = true;
    }
    function setBusy(state) {
      busy = state;
      submitBtn.disabled = state;
      submitBtn.setAttribute('aria-busy', state ? 'true' : 'false');
      submitLabel.textContent = state ? 'Locating Order…' : idleText;
    }

    function revealChildren(container) {
      // Trigger the theme's .sy-reveal entrance if the motion bootstrap is active.
      if (!document.documentElement.classList.contains('sy-motion')) return;
      var items = container.querySelectorAll('.sy-reveal');
      for (var i = 0; i < items.length; i++) {
        (function (el, delay) {
          window.setTimeout(function () { el.classList.add('is-revealed'); }, delay);
        })(items[i], i * 70);
      }
    }

    function renderResults(t) {
      results.innerHTML =
        heroHTML(t, heroImage) + etaHTML(t) + journeyHTML(t) + shipmentHTML(t) + activityHTML(t) + footerActionsHTML();

      var copyBtn = results.querySelector('[data-sy-copy]');
      if (copyBtn) {
        copyBtn.addEventListener('click', function () {
          var code = copyBtn.getAttribute('data-sy-copy') || '';
          var iconWrap = copyBtn.querySelector('.sy-track-awb__icon');
          function flash() {
            copyBtn.classList.add('is-copied');
            if (iconWrap) iconWrap.innerHTML = icon(ICONS.check);
            window.setTimeout(function () {
              copyBtn.classList.remove('is-copied');
              if (iconWrap) iconWrap.innerHTML = icon(ICONS.copy);
            }, 2000);
          }
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(code).then(flash, execCopy);
          } else {
            execCopy();
          }
          // Insecure-context / legacy fallback (mirrors sy-offers.liquid) so the
          // AWB copy button is never a silent no-op (home-mt-HF12).
          function execCopy() {
            try {
              var ta = document.createElement('textarea');
              ta.value = code;
              ta.style.position = 'fixed';
              ta.style.opacity = '0';
              document.body.appendChild(ta);
              ta.select();
              document.execCommand('copy');
              document.body.removeChild(ta);
              flash();
            } catch (err) {}
          }
        });
      }
      var resetBtn = results.querySelector('[data-sy-track-reset]');
      if (resetBtn) resetBtn.addEventListener('click', reset);
      var helpLink = results.querySelector('[data-sy-track-help]');
      if (helpLink) helpLink.setAttribute('href', contactUrl);

      formPanel.hidden = true;
      results.hidden = false;
      revealChildren(results);
      try {
        window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      } catch (e) { window.scrollTo(0, 0); }
    }

    function reset() {
      results.hidden = true;
      results.innerHTML = '';
      formPanel.hidden = false;
      clearError();
      form.reset();
      orderInput.focus();
    }

    function prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // Deep-link parity with the old Next.js /order-tracking/[orderId] route
    // (2026-07-05): ?order=1234[&email=x] prefills the form; when both fields
    // arrive the lookup fires automatically, so shared/emailed tracking links
    // land on live results instead of an empty form.
    try {
      var params = new URLSearchParams(window.location.search);
      var qpOrder = (params.get('order') || params.get('orderId') || '').trim();
      var qpEmail = (params.get('email') || '').trim();
      if (qpOrder) orderInput.value = qpOrder;
      if (qpEmail) emailInput.value = qpEmail;
      if (qpOrder && qpEmail) {
        setTimeout(function () {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { cancelable: true }));
        }, 60);
      } else if (qpOrder) {
        emailInput.focus();
      }
    } catch (e) { /* URL prefill is progressive enhancement only */ }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy) return;
      clearError();

      var orderNumber = orderInput.value.trim();
      var email = emailInput.value.trim();
      if (!orderNumber || !email) {
        showError('Please enter both order number and email address.');
        return;
      }

      setBusy(true);
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderNumber: orderNumber, email: email })
      })
        .then(function (res) {
          return res.json().then(function (data) { return { ok: res.ok, data: data }; });
        })
        .then(function (payload) {
          if (!payload.ok) {
            showError((payload.data && payload.data.error) || 'Unable to find order. Please check your details.');
            setBusy(false);
            return;
          }
          setBusy(false);
          renderResults(payload.data);
        })
        .catch(function () {
          showError('Something went wrong. Please try again.');
          setBusy(false);
        });
    });
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }
  ready(function () {
    var roots = document.querySelectorAll('[data-sy-tracking]');
    for (var i = 0; i < roots.length; i++) initTracking(roots[i]);
  });
})();
